import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { filter } from 'rxjs';
import { AuthService } from './auth.service';
import { SessionWarningSnackbarComponent } from './session-warning-snackbar.component';

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
const WARNING_LEAD_MS = 3 * 60 * 1000;
const WARNING_START_MS = INACTIVITY_TIMEOUT_MS - WARNING_LEAD_MS;
const REFRESH_THRESHOLD_MS = WARNING_LEAD_MS;

@Injectable({
  providedIn: 'root',
})
export class SessionActivityService {
  private readonly authService = inject(AuthService);
  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);

  private warningTimeout: ReturnType<typeof setTimeout> | null = null;
  private logoutTimeout: ReturnType<typeof setTimeout> | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private warningRef: MatSnackBarRef<SessionWarningSnackbarComponent> | null = null;
  private lastActivityAt = Date.now();
  private initialized = false;

  public initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    this.authService.token$.subscribe((token) => {
      if (token) {
        this.lastActivityAt = Date.now();
        this.startTracking();
        return;
      }

      this.stopTracking();
    });

    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
      this.onUserActivity();
    });

    this.document.addEventListener('pointerdown', this.handlePointerDown, true);
    this.document.addEventListener('keydown', this.handleKeyDown, true);
    this.document.addEventListener('visibilitychange', this.handleVisibilityChange, true);
  }

  private readonly handlePointerDown = () => {
    this.onUserActivity();
  };

  private readonly handleKeyDown = () => {
    this.onUserActivity();
  };

  private readonly handleVisibilityChange = () => {
    if (!this.authService.isAuthenticated() || this.document.visibilityState !== 'visible') {
      return;
    }

    this.enforceDeadlines();
  };

  private onUserActivity(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    this.lastActivityAt = Date.now();
    this.dismissWarning();
    this.scheduleTimers();
    this.refreshIfNeeded();
  }

  private refreshIfNeeded(): void {
    const expiry = this.authService.getTokenExpiryTimestamp();
    if (!expiry || expiry - Date.now() > REFRESH_THRESHOLD_MS) {
      return;
    }

    // A null response with the session still intact means the refresh failed
    // transiently (network blip / 429 / 5xx): keep the session and retry on the
    // next activity. refreshAccessToken only clears the session on definitive
    // failures (gh #242).
    this.authService.refreshAccessToken().subscribe((response) => {
      if (response || this.authService.isAuthenticated()) {
        this.scheduleTimers();
        return;
      }

      this.forceLogout();
    });
  }

  private startTracking(): void {
    this.scheduleTimers();
  }

  private stopTracking(): void {
    this.clearTimers();
    this.dismissWarning();
  }

  private scheduleTimers(): void {
    this.clearTimers();

    const elapsed = Date.now() - this.lastActivityAt;
    const warningDelay = Math.max(WARNING_START_MS - elapsed, 0);
    const logoutDelay = Math.max(INACTIVITY_TIMEOUT_MS - elapsed, 0);

    this.warningTimeout = setTimeout(() => {
      this.showWarning();
    }, warningDelay);

    this.logoutTimeout = setTimeout(() => {
      this.forceLogout();
    }, logoutDelay);
  }

  private enforceDeadlines(): void {
    const elapsed = Date.now() - this.lastActivityAt;

    if (elapsed >= INACTIVITY_TIMEOUT_MS) {
      this.forceLogout();
      return;
    }

    if (elapsed >= WARNING_START_MS) {
      this.showWarning();
      return;
    }

    this.scheduleTimers();
  }

  private showWarning(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (!this.warningRef) {
      this.warningRef = this.snackBar.openFromComponent(SessionWarningSnackbarComponent, {
        duration: undefined,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        data: { countdownLabel: this.formatRemainingTime() },
      });

      this.warningRef.onAction().subscribe(() => {
        this.onUserActivity();
      });

      this.warningRef.afterDismissed().subscribe(() => {
        this.warningRef = null;
      });
    }

    this.updateCountdown();
    this.countdownInterval ??= setInterval(() => {
      this.ngZone.run(() => {
        if (!this.authService.isAuthenticated()) {
          this.dismissWarning();
          return;
        }

        const remaining = this.getRemainingTimeMs();
        if (remaining <= 0) {
          this.forceLogout();
          return;
        }

        this.updateCountdown();
      });
    }, 1000);
  }

  private updateCountdown(): void {
    if (this.warningRef) {
      this.warningRef.instance.setCountdownLabel(this.formatRemainingTime());
    }
  }

  private getRemainingTimeMs(): number {
    return Math.max(INACTIVITY_TIMEOUT_MS - (Date.now() - this.lastActivityAt), 0);
  }

  private formatRemainingTime(): string {
    const totalSeconds = Math.ceil(this.getRemainingTimeMs() / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  private dismissWarning(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    this.warningRef?.dismiss();
    this.warningRef = null;
  }

  private clearTimers(): void {
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }

    if (this.logoutTimeout) {
      clearTimeout(this.logoutTimeout);
      this.logoutTimeout = null;
    }
  }

  private forceLogout(): void {
    this.stopTracking();
    this.authService.logout();
  }
}
